import { savedListListsQueryOptions, type SavedList } from '@/entities/saved-creators-list';
import {
  getPrimaryConstraintAction,
  getSavedListLimitDescription,
  getSavedListUsageState,
  openConstraintAction,
} from '@/entities/workspace/util/capacity-constraint-ux';
import { PageHeader } from '@/shared/components/page-header';
import { Button } from '@/shared/components/shadcn/button';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { ArrowLeft, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

export const SavedProfileListViewHeader = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { workspaceId } = useWorkspaceId();
  const { data: lists = [] } = useQuery(savedListListsQueryOptions(workspaceId));

  const limit = workspace?.constraints.influencer_saved_lists_limit;
  const usage = typeof limit === 'number' ? getSavedListUsageState(lists.length, limit) : null;
  const cta =
    workspace && usage && (usage.isNearLimit || usage.isAtLimit)
      ? getPrimaryConstraintAction(workspace.plan, workspaceId)
      : null;

  return (
    <PageHeader
      title="Manage"
      description="View and organize saved creators and exclusions"
      actions={
        usage ? (
          <>
            <TooltipFlat
              triggerClassName="text-foreground text-sm font-semibold"
              content={getSavedListLimitDescription(usage)}
            >
              {usage.currentCount} / {usage.limit} Saved lists
            </TooltipFlat>
            {cta && (
              <Button
                variant={usage.isAtLimit ? 'default' : 'outline'}
                onClick={() =>
                  openConstraintAction(cta, {
                    navigateInternal: (href) => navigate({ to: href }),
                  })
                }
              >
                {cta.label}
              </Button>
            )}
          </>
        ) : null
      }
    />
  );
};

type SavedProfileDetailViewHeaderProps = {
  listInfo: SavedList;
  selectedCreators: string[];
  handleBackToList: () => void;
  handleExportSelected: () => void;
  handleExportList: (list_id: string) => void;
};

export const SavedProfileDetailViewHeader = (props: SavedProfileDetailViewHeaderProps) => {
  const { handleBackToList, listInfo, selectedCreators, handleExportSelected, handleExportList } =
    props;

  return (
    <PageHeader
      title={listInfo.title}
      description={
        <>
          {listInfo.total_count} creators · Created{' '}
          {dayjs(listInfo.created_at).format('YYYY MMM DD')}
        </>
      }
      rowClassName="items-start"
      actionsClassName="gap-2"
      topSlot={
        <div>
          <Button variant="ghost" size="sm" onClick={handleBackToList}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Lists
          </Button>
        </div>
      }
      actions={
        <>
          {selectedCreators.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportSelected}>
              <Download className="mr-2 h-4 w-4" />
              Export Selected ({selectedCreators.length})
            </Button>
          )}
          <Button variant="default" size="sm" onClick={() => handleExportList(listInfo.id || '')}>
            <Download className="mr-2 h-4 w-4" />
            Export All
          </Button>
        </>
      }
    />
  );
};
